"use client";

import type { ReactNode } from "react";
import type {
  LeadFlowQuestionStepDefinition,
  LeadFlowStepDefinition,
  LeadFlowStepItem,
} from "../types";

import { cn } from "@homeinstead/ui";
import { Button } from "@homeinstead/ui/components/Button";
import { useCallback, useMemo, useState } from "react";
import { HEADING_QUESTION_TYPES } from "../constants/heading-question-types";
import { TAKEOVER_COLUMNS_CLASS_NAME } from "../constants/takeover-class-names";
import { useCopy } from "../hooks/use-copy";
import { getButtonVariant } from "../utils/get-button-variant";
import { useLeadFlowRuntime } from "./LeadFlowRuntimeContext";
import { LeadFlowStepProvider, useLeadFlowStep } from "./LeadFlowStepContext";
import { PrivacyDisclaimer } from "./questions";
import { StepItems } from "./StepItems";

// The legacy privacy disclaimer names this action, so the default matches it.
const DEFAULT_SUBMIT_LABEL = "Contact me with care options";

/** The takeover gives Continue more room under the answers once wide. */
const TAKEOVER_QUESTION_GAP_CLASS_NAME = "gap-4 md:gap-10";

/**
 * In the hero takeover the panel's items sit on the section surface rather
 * than a card. Below `md` the section photo follows the step instead, so the
 * panel is hidden there.
 */
const TAKEOVER_PANEL_CLASS_NAME = "hidden flex-col gap-10 md:flex";

/** Delays the panel's entrance in `lead-flow-step-transition.css`. */
const PANEL_TRANSITION_CLASS_NAME = "lead-flow-step-panel";

// Typegen marks these arrays as required, but GROQ returns null for an empty
// array and for a step whose reference did not resolve.
function getLeadFlowStepItems(
  step: LeadFlowStepDefinition,
): LeadFlowStepItem[] {
  return (step._type === "leadFlowStep" ? step.items : step.content) ?? [];
}

/**
 * A question step advances by Continue unless one of its questions
 * auto-advances on selection, in which case the selection is the action.
 */
function hasAutoAdvanceQuestion(items: LeadFlowStepItem[]): boolean {
  return items.some(
    (item) => "autoAdvance" in item && item.autoAdvance === true,
  );
}

function hasQuestionHeading(items: LeadFlowStepItem[]): boolean {
  return items.some(
    (item) =>
      HEADING_QUESTION_TYPES.has(item._type) && "label" in item && item.label,
  );
}

function hasPrivacyDisclaimer(items: LeadFlowStepItem[]): boolean {
  return items.some(
    (item) =>
      item._type === "leadFlowContactQuestion" &&
      item.showPrivacyDisclaimer === true,
  );
}

function getActionLabel(step: LeadFlowStepDefinition): string | null {
  return step._type === "leadFlowStep" ? step.actionLabel : step.continueLabel;
}

/**
 * The step's info panel, when its layout shows one. Steps in the
 * section's content slot and on a sheet never show a panel.
 */
function getInfoPanelItems(
  step: LeadFlowStepDefinition,
  canShowPanel: boolean,
): LeadFlowStepItem[] {
  if (
    !canShowPanel ||
    step._type !== "leadFlowStep" ||
    step.layout !== "question-with-panel"
  ) {
    return [];
  }

  return step.infoPanel?.content ?? [];
}

function getPanelOrderClassName(
  panelOrder: LeadFlowQuestionStepDefinition["panelOrder"],
): string {
  return cn(
    panelOrder?.small === "panel-first" ? "order-first" : "order-last",
    panelOrder?.large === "panel-first" ? "lg:order-first" : "lg:order-last",
  );
}

function ActionSpinner({ testId }: { testId: string }) {
  return (
    <span
      aria-hidden="true"
      className="mr-2 inline-block size-4 animate-spin rounded-full border-2 border-current border-t-transparent motion-reduce:animate-none"
      data-testid={testId}
    />
  );
}

type StepProps = {
  canShowPanel: boolean;
  /**
   * Section content shown above the step's own items, spaced like them. The
   * section owns it; the step only places it.
   */
  header?: ReactNode;
  isLastStep: boolean;
  /** Continue and submit wait until stored answers and prefills are read. */
  isReady: boolean;
  isSubmitPending: boolean;
  /** The submission is in flight, so submit shows its spinner. */
  isSubmitting?: boolean;
  /** The in-form Back link, when this step shows its own. */
  onBack: (() => void) | null;
  /**
   * The step's view transition classes. When set, the panel is captured as
   * its own transition group so its entrance can trail the question's.
   */
  panelTransitionClassName?: string;
  /**
   * The panel's view transition name, unique on the page. Inert previews
   * never get one, since a duplicate name would abort the transition.
   */
  panelTransitionName?: string;
  /**
   * The question and panel join a parent grid that also holds the progress
   * indicator, so the indicator and the question share one column. The parent
   * supplies that grid; this step only gives up its own box.
   */
  sharesQuestionColumn?: boolean;
  step: LeadFlowStepDefinition;
  /**
   * Names the hidden heading focus lands on when the step shows no question
   * heading of its own.
   */
  title?: string;
};

function Step({
  canShowPanel,
  header,
  isLastStep,
  isReady,
  isSubmitPending,
  isSubmitting = false,
  onBack,
  panelTransitionClassName,
  panelTransitionName,
  sharesQuestionColumn = false,
  step,
  title,
}: StepProps) {
  const stepContext = useLeadFlowStep();
  const { advance, isAnswerPendingRef, isFirstStep } = stepContext;
  const [isAnswerPending, setIsAnswerPendingState] = useState(false);
  // A pending question clears itself when it unmounts, so the ref never
  // outlives the step that set it.
  const setIsAnswerPending = useCallback(
    (nextIsAnswerPending: boolean) => {
      setIsAnswerPendingState(nextIsAnswerPending);
      if (isAnswerPendingRef) {
        isAnswerPendingRef.current = nextIsAnswerPending;
      }
    },
    [isAnswerPendingRef],
  );
  const itemsContext = useMemo(
    () => ({ ...stepContext, setIsAnswerPending }),
    [setIsAnswerPending, stepContext],
  );
  const { isSectionTakeover = false, themeContrast } = useLeadFlowRuntime();
  const copy = useCopy();
  const items = getLeadFlowStepItems(step);
  const panelItems = getInfoPanelItems(step, canShowPanel);
  const buttonVariant = getButtonVariant(themeContrast);
  const showSubmit = isLastStep && step._type === "leadFlowStep";
  const showContinue = !isLastStep && !hasAutoAdvanceQuestion(items);

  const question = (
    <div
      className={cn(
        "flex w-full min-w-0 flex-col",
        isSectionTakeover ? TAKEOVER_QUESTION_GAP_CLASS_NAME : "gap-5",
      )}
      data-lead-flow-step-id={step.id}
    >
      {/* Interstitial, contact-only, and unlabeled steps have no visible
          heading, so focus lands on one named for the step. */}
      {!isFirstStep && title && !hasQuestionHeading(items) ? (
        <h2 className="sr-only" data-lead-flow-step-heading="" tabIndex={-1}>
          {title}
        </h2>
      ) : null}
      {onBack ? (
        <button
          className="self-start font-semibold text-sm underline underline-offset-4"
          onClick={onBack}
          type="button"
        >
          {copy.backLabel}
        </button>
      ) : null}
      {header}
      <LeadFlowStepProvider value={itemsContext}>
        <StepItems items={items} />
      </LeadFlowStepProvider>
      {showContinue ? (
        <Button
          aria-busy={isAnswerPending}
          className="w-full"
          disabled={!isReady || isAnswerPending}
          loading={isAnswerPending}
          onClick={advance}
          type="button"
          variant={buttonVariant}
        >
          {isAnswerPending ? (
            <ActionSpinner testId="lead-flow-continue-spinner" />
          ) : null}
          {isAnswerPending
            ? copy.answerPendingContinueLabel
            : getActionLabel(step) || copy.continueLabel}
        </Button>
      ) : null}
      {showSubmit ? (
        // Submit keeps its disclaimer close, as on the legacy contact step.
        <div className="flex flex-col gap-4">
          <Button
            aria-busy={isSubmitPending || isAnswerPending}
            className="w-full"
            disabled={!isReady || isSubmitPending || isAnswerPending}
            loading={isSubmitPending || isAnswerPending}
            type="submit"
            variant={buttonVariant}
          >
            {isSubmitting ? (
              <ActionSpinner testId="lead-flow-submit-spinner" />
            ) : null}
            {getActionLabel(step) || DEFAULT_SUBMIT_LABEL}
          </Button>
          {hasPrivacyDisclaimer(items) ? <PrivacyDisclaimer /> : null}
        </div>
      ) : null}
    </div>
  );

  if (panelItems.length === 0 || step._type !== "leadFlowStep") {
    return question;
  }

  const panel = (
    <aside
      className={cn(
        isSectionTakeover
          ? TAKEOVER_PANEL_CLASS_NAME
          : "flex flex-col gap-4 rounded-3xl bg-surface p-6 text-surface-foreground",
        getPanelOrderClassName(step.panelOrder),
      )}
      data-lead-flow-info-panel=""
      // The card is white whatever the section paint, so its items read the
      // page surface's colours rather than the section's.
      data-surface={isSectionTakeover ? undefined : "page"}
      style={
        panelTransitionName &&
        panelTransitionClassName &&
        panelTransitionClassName !== "none"
          ? {
              viewTransitionClass: `${panelTransitionClassName} ${PANEL_TRANSITION_CLASS_NAME}`,
              viewTransitionName: panelTransitionName,
            }
          : undefined
      }
    >
      <StepItems items={panelItems} />
    </aside>
  );

  // The parent grid owns the columns, and this step must not add a box
  // between that grid and the question.
  if (sharesQuestionColumn) {
    return (
      <div className="contents">
        {question}
        {panel}
      </div>
    );
  }

  return (
    <div
      className={cn(
        "grid gap-8",
        isSectionTakeover
          ? TAKEOVER_COLUMNS_CLASS_NAME
          : "lg:grid-cols-2 lg:items-start",
      )}
    >
      {question}
      {panel}
    </div>
  );
}

export { Step };
